/**
 * AutopilotPreset — the small preset flow for the default console mode.
 * Presets are named, fixed policy bundles; each one lists its exact axes so
 * the user sees precisely what will be enforced (no hidden axes).
 */

import type { ReactElement } from "react";

export interface AutopilotPresetDef {
  readonly id: string;
  readonly title: string;
  readonly axes: readonly { readonly kind: string; readonly subject: string; readonly value: string }[];
}

export const AUTOPILOT_PRESETS: readonly AutopilotPresetDef[] = [
  {
    id: "privacy-first",
    title: "Privacy first",
    axes: [
      { kind: "training", subject: "training", value: "false" },
      { kind: "retention", subject: "prompt-retention", value: "0d" },
      { kind: "retention", subject: "abuse-retention", value: "30d" },
      { kind: "human-review", subject: "human-review", value: "false" },
      { kind: "logging", subject: "gateway-logging", value: "none" },
      { kind: "cache", subject: "provider-cache", value: "false" },
      { kind: "region", subject: "processing-region", value: "us-east-1" },
    ],
  },
  {
    id: "balanced",
    title: "Balanced",
    axes: [
      { kind: "training", subject: "training", value: "false" },
      { kind: "retention", subject: "prompt-retention", value: "0d" },
      { kind: "retention", subject: "abuse-retention", value: "90d" },
      { kind: "logging", subject: "gateway-logging", value: "summary" },
      { kind: "cache", subject: "zerofare-cache", value: "true" },
      { kind: "region", subject: "processing-region", value: "us-east-1" },
    ],
  },
];

export function AutopilotPreset({ preset, onApply }: { preset: AutopilotPresetDef; onApply: (id: string) => void }): ReactElement {
  return (
    <section className="autopilot-preset" aria-label={`Preset ${preset.title}`}>
      <h3>{preset.title}</h3>
      <ul>
        {preset.axes.map((axis) => (
          <li key={axis.subject}>
            {axis.subject}: {axis.value}
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => onApply(preset.id)}>
        Apply preset
      </button>
    </section>
  );
}
